import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { AWAITING_REVIEWS_PATH, type AwaitingReview } from '../../../../shared/contracts/review-queue.js';

/**
 * BO Queue — leg 2 of a job (owner, 25 Oct): from the field force's
 * submission to every reviewer after it, in the order the job's workflow
 * sets. It holds only what is the reader's to review at this moment
 * (`GET /v1/quality/reviews/awaiting-me`), nothing else: a job reaches the
 * next reviewer's queue only when the one before him has decided. Every row
 * opens the same review screen for every reviewer. The phone's BO Queue reads
 * the same contract.
 */
export function BoQueuePage(): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly AwaitingReview[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<readonly AwaitingReview[]>(AWAITING_REVIEWS_PATH, { token })
      .then((r) => { if (alive) setRows(Array.isArray(r) ? r : []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="bo-queue">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('bo.queue.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginBottom: 16 }}>{t('bo.queue.intro')}</p>
      {error ? <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>
        : rows === null ? <p>{t('bo.queue.loading')}</p>
        : rows.length === 0 ? <p aria-label="empty" style={{ color: 'var(--color-text-muted)' }}>{t('bo.queue.empty')}</p>
        : (
          <DataTable<AwaitingReview>
            testId="bo-queue-table"
            rowKey={(r) => r.reviewId}
            rowTestId={(r) => `row-${r.reviewId}`}
            columns={[
              { key: 'job', header: t('bo.queue.col.job'), render: (r) => (
                <Link to={`/bo/queue/${r.reviewId}`} aria-label={`open-${r.reviewId}`}>{r.jobNumber ?? '—'}</Link>
              ) },
              { key: 'vendor', header: t('bo.queue.col.vendor'), render: (r) => personLabel(r.vendorName, r.vendorCode) ?? '—' },
              { key: 'painter', header: t('bo.queue.col.fieldForce'), render: (r) => personLabel(r.painterName, r.painterCode) ?? '—' },
              { key: 'submitted', header: t('bo.queue.col.submitted'), render: (r) => at(r.submittedAt) },
              { key: 'vendorApproved', header: t('bo.queue.col.vendorApproved'), render: (r) => (r.vendorApprovedAt ? at(r.vendorApprovedAt) : '—') },
            ]}
            rows={rows}
          />
        )}
    </main>
  );
}
